import React from 'react';

// Top-of-screen chrome: mute toggle (top-left), Remote Support pill with
// jonathan presence + unread badge, and SOP button (top-right).
export default function TopBar({
  isMuted,
  setIsMuted,
  jonathanStatus,
  supportUnreadCount,
  onOpenSupport,
  onOpenSop,
  sopButtonText,
  sopButtonBgColor,
  sopButtonTextColor,
  canSeeInventory,
  onOpenInventory,
  canSeeInvoices,
  onOpenInvoices,
}) {
  const isOnline = jonathanStatus === 'online';
  const isBusy = jonathanStatus === 'busy';
  // Both "online" and "busy" get a prominent, pulsing treatment so staff can
  // clearly tell someone is reachable for live chat — busy just signals that
  // replies may be a little slower. Only "offline" stays subdued/neutral.
  const btnClasses = isOnline
    ? 'bg-gradient-to-r from-emerald-500 to-green-600 text-white ring-2 ring-emerald-300/70 shadow-emerald-500/30 hover:from-emerald-400 hover:to-green-500'
    : isBusy
      ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white ring-2 ring-amber-300/70 shadow-amber-500/30 hover:from-amber-400 hover:to-orange-500'
      : 'bg-white/90 text-gray-900 hover:bg-white';
  const pillText = isOnline
    ? 'Live Support Available'
    : isBusy
      ? 'Support busy — replies may be slower'
      : 'Support offline — leave a message';
  const dotClass = 'bg-gray-400';

  return (
    <>
      <button
        onClick={() => setIsMuted(!isMuted)}
        className="fixed top-4 left-4 z-30 p-1.5 rounded-full bg-black bg-opacity-20 hover:bg-opacity-40 transition-all text-white"
      >
        {isMuted ? (
          // Speaker with an X — clearly communicates muted state.
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-label="Unmute"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z M17 9l4 4m0-4l-4 4" /></svg>
        ) : (
          // Speaker with sound waves.
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-label="Mute"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" /></svg>
        )}
      </button>

      {canSeeInventory && onOpenInventory && (
        <button
          onClick={onOpenInventory}
          className="fixed top-4 left-16 z-30 p-1.5 rounded-full bg-black bg-opacity-20 hover:bg-opacity-40 transition-all text-white"
          title="Inventory"
          aria-label="Open inventory"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-9-4-9 4m18 0l-9 4m9-4v10l-9 4m0-10L2 7m9 4v10M2 7v10l9 4" />
          </svg>
        </button>
      )}

      {canSeeInvoices && onOpenInvoices && (
        <button
          onClick={onOpenInvoices}
          className="fixed top-4 left-28 z-30 p-1.5 rounded-full bg-black bg-opacity-20 hover:bg-opacity-40 transition-all text-white"
          title="Invoices"
          aria-label="Open invoices"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 4H7a2 2 0 01-2-2V6a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V18a2 2 0 01-2 2z" />
          </svg>
        </button>
      )}

      <div className="fixed top-4 right-4 z-30 flex items-center gap-2">
        <button
          onClick={onOpenSupport}
          className={`relative font-semibold py-2 px-4 rounded-lg shadow-md transition-all duration-150 ease-in-out ${btnClasses}`}
          title={pillText}
        >
          <span className="inline-flex items-center gap-2">
            {isOnline || isBusy ? (
              <span className="relative flex h-2.5 w-2.5">
                {/* Online keeps the attention-grabbing ping; busy shows a
                    steady white dot (still on the amber glowing button). */}
                {isOnline && (
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-80 bg-emerald-300" />
                )}
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
              </span>
            ) : (
              <span className={`w-2.5 h-2.5 rounded-full ${dotClass}`} />
            )}
            <span>Remote Support</span>
            {isOnline && (
              <span className="hidden sm:inline-flex text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full border bg-white/95 text-emerald-700 border-white/40">
                Live
              </span>
            )}
            {isBusy && (
              <span className="hidden sm:inline-flex text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full border bg-white/95 text-amber-700 border-white/40">
                Delay
              </span>
            )}
          </span>
          {supportUnreadCount > 0 && (
            <span
              className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center shadow ring-2 ring-white"
              aria-label={`${supportUnreadCount} unread ticket update${supportUnreadCount > 1 ? 's' : ''}`}
            >
              {supportUnreadCount > 9 ? '9+' : supportUnreadCount}
            </span>
          )}
        </button>
        <button
          onClick={onOpenSop}
          className="font-semibold py-2 px-4 rounded-lg shadow-md transition-colors duration-150 ease-in-out"
          style={{ backgroundColor: sopButtonBgColor, color: sopButtonTextColor }}
        >
          {sopButtonText}
        </button>
      </div>
    </>
  );
}
